import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '@/api/clients'
import { qk } from '@/api/queryKeys'
import { useSelfId } from '@/auth/session'
import { Badge } from '@/components/Badge'
import { Countdown } from '@/components/Countdown'
import { CurrencyReadout } from '@/components/CurrencyReadout'
import { FactionStripe } from '@/components/FactionStripe'
import { Meter } from '@/components/Meter'
import { healthTone } from '@/components/tones'
import { Stat } from '@/components/Stat'
import { BuildingStatus, type BuildingInfo, type TierBenefits } from '@/gen/building/v1/building_pb'
import { formatAgo, toMs } from '@/lib/format'
import { useNow } from '@/lib/useNow'
import { factionToken, isOwned } from '@/map/geojson'
import type { Selection } from '@/map/selection'
import { buildingStatusKey, buildingStatusTone, factionName, humanize } from './labels'
import { PanelError, PanelLoading, Section } from './PanelState'

/** Buildings have 100 HP (decay and resets both work on that scale). */
export const BUILDING_MAX_HEALTH = 100

export function BuildingPanel({ id, onSelect }: { id: string; onSelect: (s: Selection) => void }) {
  const selfId = useSelfId()
  const q = useQuery({
    queryKey: qk.building(id),
    queryFn: ({ signal }) => api().building.getBuilding({ buildingId: id }, { signal }),
    refetchInterval: 30_000,
  })
  if (q.isPending) return <PanelLoading />
  if (q.isError) return <PanelError error={q.error} />
  return <BuildingDossier building={q.data} selfId={selfId} onOpenPlayer={(pid) => onSelect({ kind: 'player', id: pid })} />
}

function BenefitsTable({ current, next }: { current: TierBenefits; next: TierBenefits }) {
  const { t } = useTranslation()
  const rows: { key: string; cur: string; nxt: string }[] = [
    { key: 'income', cur: `${current.incomeMultPct}%`, nxt: `${next.incomeMultPct}%` },
    { key: 'decay', cur: `−${current.decayReductionPct}%`, nxt: `−${next.decayReductionPct}%` },
    { key: 'challenge', cur: `+${current.challengeBonusPct}%`, nxt: `+${next.challengeBonusPct}%` },
  ]
  return (
    <table className="mt-3 w-full text-[13px]" data-testid="tier-benefits">
      <thead>
        <tr className="text-left">
          <th className="soc-label pb-1.5 font-normal" />
          <th className="soc-label pb-1.5 text-right font-normal">{t('building.tierN', { tier: current.tier })}</th>
          <th className="soc-label pb-1.5 text-right font-normal text-brass">{t('building.tierN', { tier: next.tier })}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-t border-border">
            <td className="py-1.5 text-ink-soft">{t(`building.benefit.${r.key}`)}</td>
            <td className="soc-num py-1.5 text-right text-ink-soft">{r.cur}</td>
            <td className="soc-num py-1.5 text-right text-ink">{r.nxt}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export interface BuildingDossierProps {
  building: BuildingInfo
  selfId: string | null
  onOpenPlayer: (playerId: string) => void
}

/**
 * The building dossier. Per-viewer fields (collect timer, next tier cost and
 * benefits, the challenge clock) are masked server-side and rendered only
 * when present.
 *
 * PRIVACY: last_visited_at and claimed_at are stamped only by IN-PERSON
 * actions, so on somebody else's building they reveal roughly when a real
 * person last stood there. They render ONLY when the viewer owns the building.
 */
export function BuildingDossier({ building: b, selfId, onOpenPlayer }: BuildingDossierProps) {
  const { t, i18n } = useTranslation()
  const now = useNow(1000)
  const owned = isOwned(b)
  const mine = Boolean(selfId) && b.ownerPlayerId === selfId
  const stripe = owned ? factionToken(b.ownerFaction) : 'fx-neutral'
  const collectAt = b.collectReadyAtUnixMs !== undefined ? toMs(b.collectReadyAtUnixMs) : null
  const lastVisited = mine && b.lastVisitedAtUnixMs !== undefined ? toMs(b.lastVisitedAtUnixMs) : null
  const claimedAt = mine && b.claimedAtUnixMs !== undefined ? toMs(b.claimedAtUnixMs) : null
  const nextCost = toMs(b.nextTierCost)
  const kicker = [humanize(b.gameType), humanize(b.category)].filter(Boolean).join(' · ')

  return (
    <div data-testid="building-dossier">
      <div className="relative px-5 pt-5 pb-4">
        <FactionStripe token={stripe} />
        {kicker && <p className="soc-label">{kicker}</p>}
        <h2 className="soc-proper mt-1.5 text-[28px] leading-tight text-ink" data-testid="building-name">
          {b.name || t('building.unnamed')}
        </h2>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone={buildingStatusTone(b.status)}>{t(`building.status.${buildingStatusKey(b.status)}`)}</Badge>
          {mine && <Badge tone="ink">{t('building.yours')}</Badge>}
          {b.hasQuestsAvailable && <Badge tone="brass">{t('building.questsHere')}</Badge>}
        </div>
        {b.status === BuildingStatus.DESTROYED && b.rebuildAtUnixMs !== undefined && (
          <p className="mt-3 text-[14px] text-ink-soft">
            {t('building.rebuildsIn')} <Countdown target={toMs(b.rebuildAtUnixMs)} done={t('building.rebuildingNow')} className="text-danger" />
          </p>
        )}
        {b.status === BuildingStatus.CHALLENGED && (
          <p className="mt-3 text-[14px] text-ink-soft" data-testid="challenge-line">
            {b.challengeEndsAtUnixMs !== undefined ? (
              <>
                {t('building.challengeEndsIn')}{' '}
                <Countdown target={toMs(b.challengeEndsAtUnixMs)} done={t('building.challengeSettling')} className="text-warning" />
              </>
            ) : (
              t('building.underChallenge')
            )}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-5 gap-y-4 border-t border-border px-5 py-4">
        <Stat label={t('building.tier')} value={b.tier > 0 ? `T${b.tier}` : '—'} />
        <div>
          <div className="soc-label">{t('building.health')}</div>
          <div className="mt-1 flex items-center gap-2">
            <Meter value={b.health} max={BUILDING_MAX_HEALTH} tone={healthTone(b.health, BUILDING_MAX_HEALTH)} className="flex-1" label={t('building.health')} />
            <span className="soc-num text-[13px] text-ink-soft">{b.health}</span>
          </div>
        </div>
        <div>
          <div className="soc-label">{t('building.influence')}</div>
          <div className="mt-1">
            <CurrencyReadout currency="influence" amount={b.influence} />
          </div>
        </div>
        <div>
          <div className="soc-label">{t('building.income')}</div>
          <div className="mt-1">
            {toMs(b.tierIncome) > 0 ? (
              <CurrencyReadout currency="dirty" amount={b.tierIncome} suffix={t('building.perDay')} />
            ) : (
              <span className="text-ink-mute">—</span>
            )}
          </div>
        </div>
      </div>

      <Section title={t('building.heldBy')}>
        {owned && b.ownerPlayerId ? (
          <button
            type="button"
            onClick={() => onOpenPlayer(b.ownerPlayerId)}
            className="group flex w-full items-center justify-between gap-3 border border-border bg-panel px-3 py-2.5 text-left hover:border-border-light hover:bg-panel-light"
            data-testid="owner-link"
          >
            <span className="min-w-0">
              <span className="soc-proper block truncate text-[19px] text-ink group-hover:text-ink">
                {b.ownerPlayerName || t('player.unnamed')}
              </span>
              <span className="soc-label mt-0.5 block" style={{ color: `var(--soc-${stripe})` }}>
                {factionName(t, b.ownerFaction)}
              </span>
            </span>
            <span className="soc-label shrink-0 text-ink-soft group-hover:text-ink">{t('building.openDossier')} →</span>
          </button>
        ) : owned ? (
          <p className="soc-label" style={{ color: `var(--soc-${stripe})` }}>
            {factionName(t, b.ownerFaction)}
          </p>
        ) : (
          <p className="soc-flavour text-[15px]">{t('building.nobodyHolds')}</p>
        )}
      </Section>

      {(mine || collectAt !== null || nextCost > 0) && (
        <Section title={t('building.yourHolding')} testId="owner-section">
          <div className="flex flex-col gap-3">
            {collectAt !== null && (
              <div className="flex items-center justify-between gap-3" data-testid="collect-line">
                <span className="text-ink-soft">{t('building.collect')}</span>
                {collectAt <= now ? (
                  <Badge tone="success">{t('building.collectReady')}</Badge>
                ) : (
                  <span className="text-[14px] text-ink-soft">
                    {t('building.collectIn')} <Countdown target={collectAt} done={t('building.collectReady')} className="text-ink" />
                  </span>
                )}
              </div>
            )}
            {nextCost > 0 && (
              <div data-testid="next-tier">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-ink-soft">{t('building.nextTier', { tier: b.tier + 1 })}</span>
                  <CurrencyReadout currency="dirty" amount={b.nextTierCost} />
                </div>
                {b.currentTierBenefits && b.nextTierBenefits && (
                  <BenefitsTable current={b.currentTierBenefits} next={b.nextTierBenefits} />
                )}
              </div>
            )}
            {mine && nextCost === 0 && b.tier > 0 && <p className="text-[14px] text-ink-mute">{t('building.maxTier')}</p>}
            {claimedAt !== null && claimedAt > 0 && (
              <div className="flex items-center justify-between gap-3 text-[14px]">
                <span className="text-ink-soft">{t('building.claimed')}</span>
                <span className="soc-num text-ink-soft">{new Date(claimedAt).toLocaleDateString(i18n.resolvedLanguage)}</span>
              </div>
            )}
            {lastVisited !== null && lastVisited > 0 && (
              <div className="flex items-center justify-between gap-3 text-[14px]" data-testid="last-visited">
                <span className="text-ink-soft">{t('building.lastVisited')}</span>
                <span className="text-ink-soft">{formatAgo(now - lastVisited)}</span>
              </div>
            )}
          </div>
        </Section>
      )}
    </div>
  )
}
